import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { PermissionKey } from '@/auth/permissions';
import { useSession } from '@/auth/session';

/** Full-screen hold while the initial /auth/me settles. */
function SessionSplash() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-primary"
          role="status"
          aria-label="Loading"
        />
        <p className="font-display text-2xl text-muted-foreground">{t('appName')}</p>
      </div>
    </div>
  );
}

/** Requires a signed-in user. Remembers where they were headed. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useSession();
  const location = useLocation();

  if (loading) return <SessionSplash />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Requires a permission. Sends the user to the dashboard rather than showing a
 * bare 403 — they arrived via a link or a bookmark, not an attack.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: PermissionKey;
  children: ReactNode;
}) {
  const { can, loading } = useSession();

  if (loading) return <SessionSplash />;
  if (!can(permission)) return <Navigate to="/" replace />;

  return <>{children}</>;
}

export function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { isSuperadmin, loading } = useSession();

  if (loading) return <SessionSplash />;
  if (!isSuperadmin) return <Navigate to="/" replace />;

  return <>{children}</>;
}

/**
 * Renders children only when the permission is held.
 *
 * ```tsx
 * <Can permission={PERMISSIONS.medicinesCreate}>
 *   <Button>Add medicine</Button>
 * </Can>
 * ```
 */
export function Can({
  permission,
  fallback = null,
  children,
}: {
  permission: PermissionKey;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { can } = useSession();
  return <>{can(permission) ? children : fallback}</>;
}
